// ─── Payment, fee, cancellation and trust copy — the ONE source ───────────────
// Every public sentence about money, fees, cancellation or ratings comes from
// here, so the homepage, browse page, enquiry form, /help/schools, /terms and
// the static SEO pages (scripts/build-content.js) cannot drift apart again.
// Before this file the site said "SSL encrypted payments", "we handle payouts"
// and "no payment is taken on YogaStay" on the same page.
//
// CommonJS on purpose: the Node generator require()s it and the React app
// imports it through webpack's interop. Plain strings and pure functions only.
//
// Decisions behind these values: YOGASTAY_IMPROVEMENT_PLAN.md Part B.
// YogaStay is enquiry-only (no payments, for Indian cross-border remittance
// rules). Never add payment, refund, commission or guarantee terms here that
// are not in Part B.

// ── The enquiry model ────────────────────────────────────────────────────────
const ENQUIRY_FLOW =
  "Send a free enquiry → the school confirms → you pay the school directly.";
const NO_PAYMENT_LINE =
  "YogaStay never takes payment and charges no commission.";
const ENQUIRY_FLOW_FULL = `${ENQUIRY_FLOW} ${NO_PAYMENT_LINE}`;

// Shown as one line where space is tight, or as three items in a trust bar.
const TRUST_STRIP = "Verified schools · Free enquiry · Pay the school directly";
const TRUST_ITEMS = [
  { key: "verified", label: "Verified schools",        sub: "Every school manually reviewed" },
  { key: "enquiry",  label: "Free enquiry",            sub: "No booking fee, no commitment" },
  { key: "direct",   label: "Pay the school directly", sub: "YogaStay never takes payment" },
];

// How fast a school answers an enquiry (decided 18 Oct 2026: 48 working
// hours). supabase/functions/booking-request can't import this file (Deno), so
// it repeats the phrase — change both together.
const REPLY_PROMISE = "within 2 working days";

// ── Schools ──────────────────────────────────────────────────────────────────
const SCHOOL_PROMISE = "Free to list. Free enquiries. No commission.";
const SCHOOL_PITCH =
  "List your school for free and receive enquiries from students worldwide. Students pay your school directly, on your own terms — YogaStay never takes payment and charges no commission.";

// ── Cancellation and payment terms (set per programme by the school) ─────────
const SCHOOL_TERMS_NOTE = "Set by the school. You pay the school directly.";
const CANCELLATION_UNSET = "Ask the school for its cancellation terms.";
const PAYMENT_TERMS_UNSET = "Ask the school for its payment terms.";
const REFUNDS_LINE =
  "Payment, cancellation and refunds are agreed directly with the school. YogaStay does not take payment, so it cannot issue refunds.";

// The retreat form used to pre-fill this sentence, so every retreat carries it
// whether or not the school ever chose it. It is not the school's policy and
// must never be shown as one (decided 18 Oct 2026).
const LEGACY_DEFAULT_CANCELLATION = "Full refund if cancelled 30 days before start date.";

/** The school's own cancellation text, or null when the school has not set one. */
function cancellationTerms(text) {
  const t = String(text == null ? "" : text).trim();
  if (!t || t === LEGACY_DEFAULT_CANCELLATION) return null;
  return t;
}

/**
 * Whether a school has confirmed its class and stay cancellation policies
 * (schools.cancellation_confirmed_at, migration 20261025). The stored type
 * defaults to "moderate" at the column level, so until the school confirms,
 * that value is not shown as the school's policy.
 */
function schoolPolicyConfirmed(school) {
  return !!(school && school.cancellation_confirmed_at);
}

/**
 * What a school still has to confirm before students see its own terms.
 * Mirrors the nudge rules in pending_school_nudges() (migration 20261025).
 * @returns {{ classesAndStays: boolean, retreats: Array<{ retreat: object, cancellation: boolean, payment: boolean }> }}
 *          cancellation/payment = true means that part is MISSING.
 */
function termsNeedingConfirmation({ school, classes = [], retreats = [], today = new Date().toISOString().slice(0, 10) }) {
  const hasClassesOrStays = classes.length > 0 || !!(school && school.accommodation_enabled);
  const live = retreats.filter((r) => r.is_active && !r.is_draft && (!r.end_date || r.end_date >= today));
  return {
    classesAndStays: hasClassesOrStays && !schoolPolicyConfirmed(school),
    retreats: live
      .map((r) => ({ retreat: r, cancellation: !cancellationTerms(r.cancellation_policy), payment: !paymentTerms(r.payment_terms) }))
      .filter((x) => x.cancellation || x.payment),
  };
}

/** The school's own payment terms, or null when unset. */
function paymentTerms(text) {
  const t = String(text == null ? "" : text).trim();
  return t || null;
}

// ── Ratings ──────────────────────────────────────────────────────────────────
// Below this many reviews a school shows NEW_LABEL instead of a number. A
// rating from two reviews is noise, and "0" next to a school reads as bad.
const REVIEW_MIN_TO_SHOW_RATING = 7;
const NEW_LABEL = "New on YogaStay";

/**
 * Whether to show a rating, and what. Never returns a 0 rating.
 * @returns {{ show: true, rating: number, count: number, text: string } | { show: false, text: string }}
 */
function ratingDisplay(rating, reviewCount) {
  const r = Number(rating);
  const n = Number(reviewCount) || 0;
  if (n >= REVIEW_MIN_TO_SHOW_RATING && r > 0) {
    const rounded = Math.round(r * 10) / 10;
    return { show: true, rating: rounded, count: n, text: `★ ${rounded.toFixed(1)} (${n} reviews)` };
  }
  return { show: false, text: NEW_LABEL };
}

// ── Destination counts ───────────────────────────────────────────────────────
// Counts appear only once a destination has this many VERIFIED schools; below
// it the tile shows its tagline only. Small numbers undersell the platform and
// big fake ones were misleading.
const DESTINATION_COUNT_MIN = 10;

/** "12 schools · 30 programmes", or null when the count should stay hidden. */
function destinationCountLabel(schools, programmes) {
  const s = Number(schools) || 0;
  const p = Number(programmes) || 0;
  if (s < DESTINATION_COUNT_MIN) return null;
  const parts = [`${s} verified schools`];
  if (p > 0) parts.push(`${p} ${p === 1 ? "programme" : "programmes"}`);
  return parts.join(" · ");
}

/**
 * Verified schools and live programmes per destination name. A school counts
 * when the name appears in its location ("Bali (Ubud), Indonesia" counts for
 * Bali and for Ubud); a programme counts when its school is in `schools` (pass
 * verified schools only) and either its own location or its school's matches.
 * @returns {{ [name: string]: { schools: number, programmes: number } }}
 */
function countByDestination(schools, programmes, names) {
  const where = (x) => [x.location, x.city, x.region, x.country].filter(Boolean).join(" ").toLowerCase();
  const byId = Object.create(null);
  schools.forEach((s) => { byId[s.id] = s; });
  const out = Object.create(null);
  names.forEach((name) => {
    const k = name.toLowerCase();
    out[name] = {
      schools: schools.filter((s) => where(s).includes(k)).length,
      programmes: programmes.filter((r) => byId[r.school_id] && (where(r).includes(k) || where(byId[r.school_id]).includes(k))).length,
    };
  });
  return out;
}

// ── FAQ (homepage; mirrored in public/index.html JSON-LD — a test keeps them equal) ──
const STUDENT_FAQ = [
  { q: "How does booking work?",
    a: `YogaStay is enquiry-only. Choose a retreat, class or room and send a free enquiry with your dates. The school contacts you, usually ${REPLY_PROMISE}, to confirm availability, and you pay the school directly. YogaStay never takes payment and charges no commission.` },
  { q: "Do I pay on YogaStay?",
    a: "No. No payment is taken on YogaStay. You pay the school directly, using the payment method the school offers. YogaStay never asks you to send money to us — if anyone claiming to be YogaStay asks for payment, please write to support@yogastay.app." },
  { q: "What about cancellations and refunds?",
    a: "Each school sets its own cancellation and payment terms, shown on the programme where the school has added them. Because you pay the school directly, refunds are agreed with the school — YogaStay cannot issue refunds. Ask the school to confirm its terms in writing before you pay." },
];
const GENERAL_FAQ = [
  { q: "What's usually included in the price?",
    a: "It varies by school. Many retreats include accommodation, daily yoga classes and some meals. Each listing shows what's included, and the school confirms the details when it replies to your enquiry." },
  { q: "Do I need to be an experienced yogi?",
    a: "Not at all. Schools list the level they cater to — from complete beginners to advanced practitioners and teacher trainees. Use the style and level filters to find the right fit." },
];
const SCHOOL_FAQ = [
  { q: "How do I list my yoga school?",
    a: `Tap “List your school”, create your school profile, and submit it for verification. ${SCHOOL_PROMISE}` },
  { q: "How do schools get paid?",
    a: "Directly by the student. YogaStay sends you each enquiry by email; you contact the student, confirm availability and collect payment yourself, under your own cancellation and refund terms." },
  { q: "How quickly can a school go live?",
    a: "Most schools are reviewed within 24 hours of submitting a complete profile, after which the listing is visible to students." },
];
const HOME_FAQ = [...STUDENT_FAQ, ...GENERAL_FAQ, ...SCHOOL_FAQ];

module.exports = {
  ENQUIRY_FLOW, NO_PAYMENT_LINE, REPLY_PROMISE, ENQUIRY_FLOW_FULL, TRUST_STRIP, TRUST_ITEMS,
  SCHOOL_PROMISE, SCHOOL_PITCH,
  SCHOOL_TERMS_NOTE, CANCELLATION_UNSET, PAYMENT_TERMS_UNSET, REFUNDS_LINE,
  LEGACY_DEFAULT_CANCELLATION, cancellationTerms, paymentTerms, schoolPolicyConfirmed, termsNeedingConfirmation,
  REVIEW_MIN_TO_SHOW_RATING, NEW_LABEL, ratingDisplay,
  DESTINATION_COUNT_MIN, destinationCountLabel, countByDestination,
  STUDENT_FAQ, GENERAL_FAQ, SCHOOL_FAQ, HOME_FAQ,
};
